import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Search,
  Send,
  X,
  XCircle,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import ConfirmDialog from "components/ConfirmDialog";
import CopyLinkButton from "components/MidApplications/CopyLinkButton";
import {
  buildPreflightView,
  mergeRefusal,
  normalizeMccRecords,
  mccOptionLabel,
  shouldSearchMcc,
  transmitBody,
  describeTransmitError,
  describeTransmitSuccess,
  normalizeSkipped,
  describeMccSearchError,
  isStaleTemplateError,
  canTransmit,
  MCC_REQUIRED_HINT,
} from "utilities/stratusPreflight";
import {
  transmitTemplateOptions,
  resolveTemplateSelection,
  templateIdForRequest,
  templateLabel,
} from "utilities/pricingTemplates";
import {
  isSelectable,
  reconcileSelection,
  toggleSelection,
  selectionCount,
  selectedFileIds,
  uploadedLabel,
} from "utilities/stratusDocumentSelection";

/**
 * SOUND-776 — Transmit a MID application to Kurv (Stratus).
 *
 * Flow: open → dry run → preflight panel (blocking vs warnings) + preview →
 * pick an MCC (re-runs the dry run) → signature toggle → "Review & transmit"
 * (disabled while anything blocks) → ConfirmDialog naming the merchant and Kurv
 * → POST ?live=true → result screen.
 *
 * Every decision is in utilities/stratusPreflight.js (tested). This file only
 * renders. Chrome follows TicketCreateModal: portal to body, fixed header,
 * scrolling body, max-h 88vh.
 *
 * NOTHING HERE RENDERS A RAW RESPONSE. Errors go through describeTransmitError,
 * which yields a title, a sentence and a list of field errors — never a JSON blob.
 *
 * Props:
 *   open, onClose
 *   application   the MID application record (applicationId, clientName, dbaName)
 *   onTransmitted called after a 201 or a 409, so the page can refresh silently
 */

const MCC_DEBOUNCE_MS = 350;

function Heading({ children, required = false }) {
  return (
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
      {required && <span className="ml-0.5 text-destructive">*</span>}
    </h4>
  );
}

/** The inline "required" hint under an empty required field. */
function RequiredHint({ children, href }) {
  return (
    <p className="mt-1 text-xs text-warning">
      {children}
      {href && (
        <>
          {" "}
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
          >
            Open the processor page <ExternalLink className="h-3 w-3" />
          </a>
        </>
      )}
    </p>
  );
}

function IssueList({ items, tone }) {
  const Icon = tone === "destructive" ? XCircle : AlertTriangle;
  const color = tone === "destructive" ? "text-destructive" : "text-warning";
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={`${it.field}-${i}`} className="flex items-start gap-2 text-sm">
          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${color}`} />
          <div className="min-w-0">
            <div className="font-medium text-foreground">
              {it.label}
              {it.nameSplit && (
                <span className="badge badge-warning ml-2 align-middle text-[10px]">Name split</span>
              )}
            </div>
            {it.detail && <div className="text-xs text-muted-foreground">{it.detail}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function SkippedList({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 rounded-md bg-warning/5 p-2 ring-1 ring-warning/30">
      <div className="mb-1 text-xs font-medium text-foreground">
        Not sent to Kurv ({items.length}) — upload by hand in the Kurv portal if needed
      </div>
      <ul className="space-y-0.5">
        {items.map((d, i) => (
          <li key={`${d.filename}-${i}`} className="flex items-start gap-1.5 text-xs">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
            <span className="min-w-0 text-foreground">
              {d.filename || d.type || "Document"}
              {d.reason && <span className="text-muted-foreground"> — {d.reason}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Preview({ preview, skipped, selection, onToggle, disabled }) {
  if (!preview) return null;
  const owners = preview.ownerNames || [];
  const docs = preview.documents || [];
  // SOUND-830: pick which eligible documents go. Older API = no fileIds = the
  // read-only list (everything eligible is sent).
  const selectable = isSelectable(docs);
  const count = selectionCount(docs, selection);
  return (
    <section>
      <Heading>What Kurv will receive</Heading>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">Legal name</dt>
          <dd className="text-foreground">{preview.legalName || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">DBA</dt>
          <dd className="text-foreground">{preview.dbaName || "—"}</dd>
        </div>
      </dl>

      <div className="mt-3">
        <div className="mb-1 text-xs text-muted-foreground">Owners</div>
        {owners.length === 0 ? (
          <p className="text-sm text-muted-foreground">No owners on record.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md ring-1 ring-border">
            {owners.map((o) => (
              <li key={o.ownerId || o.fullName} className="px-3 py-2 text-sm">
                <div className="text-foreground">{o.fullName || "(unnamed)"}</div>
                <div className="text-xs text-muted-foreground">
                  First: <span className="text-foreground">{o.firstName || "—"}</span>
                  {" · "}Last: <span className="text-foreground">{o.lastName || "—"}</span>
                  {" · "}Title:{" "}
                  <span className={o.title ? "text-foreground" : "text-destructive"}>
                    {o.title || "not set"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-3">
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
          <span>{selectable ? "Documents to upload" : "Documents that will upload"}</span>
          {selectable && (
            <span className={count.selected === 0 ? "text-warning" : undefined}>
              {count.selected} of {count.total} selected
            </span>
          )}
        </div>
        {docs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents will be uploaded.</p>
        ) : selectable ? (
          <>
            <ul className="divide-y divide-border rounded-md ring-1 ring-border">
              {docs.map((d) => {
                const uploaded = uploadedLabel(d.uploadedAt);
                return (
                  <li key={d.fileId}>
                    <label className="flex cursor-pointer items-start gap-2.5 px-3 py-2 text-sm hover:bg-muted/30">
                      <input
                        type="checkbox"
                        checked={Boolean(selection?.[d.fileId])}
                        onChange={(e) => onToggle(d.fileId, e.target.checked)}
                        disabled={disabled}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-brand focus:ring-brand/40"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-foreground">{d.filename || d.type}</span>
                          {d.stratusCategory && (
                            <span className="badge shrink-0 text-[10px]">{d.stratusCategory}</span>
                          )}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {uploaded || "Upload date unknown"}
                          {d.defaultSelected === false && " · older file — not selected by default"}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {count.selected === 0 && (
              <p className="mt-1 text-xs text-warning">No documents selected — the application will be sent without documents.</p>
            )}
          </>
        ) : (
          <ul className="space-y-1">
            {docs.map((d, i) => (
              <li key={`${d.filename}-${i}`} className="flex items-center gap-2 text-sm">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate text-foreground">{d.filename || d.type}</span>
                {d.stratusCategory && (
                  <span className="badge shrink-0 text-[10px]">{d.stratusCategory}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <SkippedList items={skipped} />
      </div>
    </section>
  );
}

function MccPicker({ selected, onSelect, disabled, hint }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const userRef = useRef(auth.user);
  userRef.current = auth.user;

  useEffect(() => {
    if (!shouldSearchMcc(query)) {
      // Invalidate anything still in flight so a late answer cannot repopulate
      // a list the operator has already cleared.
      seq.current += 1;
      setResults([]);
      setError(null);
      setLoading(false);
      return undefined;
    }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.stratusMccSearch(query.trim()),
          userRef.current,
          effectiveCompanyId
        );
        if (mine === seq.current) setResults(normalizeMccRecords(res.data));
      } catch (err) {
        if (mine === seq.current) {
          setResults([]);
          // SOUND-784: say WHY — a 503 with no Stratus token on the server is
          // a config problem, not something "try again" fixes.
          setError(describeMccSearchError(err.response?.status, err.response?.data));
        }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, MCC_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, effectiveCompanyId]);

  return (
    <section>
      <Heading required>Merchant category (MCC)</Heading>
      {selected ? (
        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2 ring-1 ring-border">
          <span className="min-w-0 text-sm text-foreground">{mccOptionLabel(selected)}</span>
          <button
            type="button"
            className="btn btn-ghost h-7 shrink-0 px-2 text-xs"
            onClick={() => onSelect(null)}
            disabled={disabled}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              className="input w-full py-1.5 pl-8 text-sm"
              placeholder="Search by code or description, e.g. 5999 or supplements"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={disabled}
            />
            {loading && (
              <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
          {!loading && !error && shouldSearchMcc(query) && results.length === 0 && (
            <p className="mt-1 text-xs text-muted-foreground">No matching categories.</p>
          )}
          {results.length > 0 && (
            <ul className="mt-2 max-h-48 divide-y divide-border overflow-y-auto rounded-md ring-1 ring-border">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted/50"
                    onClick={() => {
                      onSelect(r);
                      setQuery("");
                      setResults([]);
                    }}
                  >
                    {r.mcc && <span className="mr-2 font-mono text-foreground">{r.mcc}</span>}
                    <span className="text-foreground">{r.category || "—"}</span>
                    {r.description && (
                      <span className="block text-xs text-muted-foreground">{r.description}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {!selected && <RequiredHint>{hint || MCC_REQUIRED_HINT}</RequiredHint>}
    </section>
  );
}

function ErrorPanel({ error }) {
  if (!error) return null;
  return (
    <div className="rounded-lg bg-destructive/5 p-3 ring-1 ring-destructive/30" role="alert">
      <div className="flex items-start gap-2">
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">{error.title}</div>
          <p className="mt-0.5 text-sm text-muted-foreground">{error.message}</p>
          {error.fieldErrors?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {error.fieldErrors.map((fe, i) => (
                <li key={`${fe.field}-${i}`} className="text-sm">
                  <span className="font-medium text-foreground">{fe.label}:</span>{" "}
                  <span className="text-muted-foreground">{fe.messages.join(" ")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessView({ success }) {
  const { documents } = success;
  return (
    <div className="space-y-4">
      {/* The deal exists at Kurv, but the CRM status write failed — the record
          still reads its old status. Loud, because nothing else will say so. */}
      {success.statusNotUpdated && (
        <div className="flex items-start gap-3 rounded-lg bg-warning/10 p-3 ring-1 ring-warning/40" role="alert">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div>
            <div className="text-sm font-semibold text-foreground">The CRM status did not update</div>
            <p className="text-sm text-muted-foreground">
              Kurv has the application, but this record still reads
              {success.status ? ` "${success.status}"` : " its previous status"}. Set it to
              Submitted by hand from the Actions panel — do not transmit again.
            </p>
            {success.statusError && (
              <p className="mt-1 text-xs text-muted-foreground">Reason: {success.statusError}</p>
            )}
          </div>
        </div>
      )}
      <div className="flex items-start gap-3 rounded-lg bg-success/10 p-3 ring-1 ring-success/30">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
        <div>
          <div className="text-sm font-semibold text-foreground">Submitted to Kurv</div>
          <p className="text-sm text-muted-foreground">
            {success.signatureSent
              ? "Kurv has sent the merchant the application to sign."
              : "No signature request was sent."}
          </p>
        </div>
      </div>
      {/* Two links (CPO): the AGENT link opens; the MERCHANT link is copy-only.
          No agent link means no primary button — never the merchant link. */}
      {success.agentUrl ? (
        <a
          href={success.agentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline w-full justify-center gap-2"
        >
          <ExternalLink className="h-4 w-4" />
          Open in Kurv
        </a>
      ) : (
        <p className="text-sm text-muted-foreground">Kurv did not return an agent link for this application.</p>
      )}
      <CopyLinkButton url={success.merchantUrl} className="w-full" />
      <section>
        <Heading>Documents</Heading>
        {documents.uploaded.length === 0 && documents.errors.length === 0 && documents.skipped.length === 0 && (
          <p className="text-sm text-muted-foreground">No documents were uploaded.</p>
        )}
        {documents.uploaded.length > 0 && (
          <ul className="space-y-1">
            {documents.uploaded.map((d, i) => (
              <li key={`${d.filename}-${i}`} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />
                <span className="truncate text-foreground">{d.filename || d.type || "Document"}</span>
                {d.stratusCategory && <span className="badge shrink-0 text-[10px]">{d.stratusCategory}</span>}
              </li>
            ))}
          </ul>
        )}
        {documents.errors.length > 0 && (
          <ul className="mt-2 space-y-1 rounded-md bg-destructive/5 p-2 ring-1 ring-destructive/30">
            {documents.errors.map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                <span className="text-foreground">
                  {e.filename ? <span className="font-medium">{e.filename}: </span> : null}
                  {e.message}
                  {e.stratusCategory && (
                    <span className="text-muted-foreground"> ({e.stratusCategory})</span>
                  )}
                </span>
              </li>
            ))}
            <li className="pt-1 text-xs text-muted-foreground">
              The application was submitted; upload these documents in the Kurv portal.
            </li>
          </ul>
        )}
        <SkippedList items={documents.skipped} />
      </section>
    </div>
  );
}

export default function StratusTransmitModal({ open, onClose, application, onTransmitted }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [dryRun, setDryRun] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [mcc, setMcc] = useState(null);
  const [sendForSignature, setSendForSignature] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [success, setSuccess] = useState(null);
  // SOUND-784: the operator's own template pick; null = let the server choose
  // (its selectedPricingTemplateId, i.e. the processor default).
  const [pickedTemplate, setPickedTemplate] = useState(null);
  // Shown above the select when the API refused a stale pick (deactivated or
  // removed on the processor since the list was loaded).
  const [templateNotice, setTemplateNotice] = useState(null);
  // SOUND-830: fileId -> ticked. Reconciled on every dry run so an MCC or
  // template re-check keeps the operator's picks.
  const [docSelection, setDocSelection] = useState({});
  const seq = useRef(0);
  // Read through a ref: the auth object can be replaced (token refresh) while
  // the dialog is open, and making the dry run depend on it would re-run the
  // open effect below and wipe the operator's MCC choice mid-flow.
  const userRef = useRef(auth.user);
  userRef.current = auth.user;
  const onTransmittedRef = useRef(onTransmitted);
  onTransmittedRef.current = onTransmitted;

  const applicationId = application?.applicationId;

  const runDryRun = useCallback(
    async ({ mcc: selectedMcc = null, pricingTemplateId = null } = {}) => {
      if (!applicationId) return;
      const mine = ++seq.current;
      setChecking(true);
      setCheckError(null);
      try {
        const res = await makeApiRequest(
          "post",
          apiRoutes.stratusTransmit(applicationId),
          userRef.current,
          effectiveCompanyId,
          transmitBody({ mcc: selectedMcc, sendForSignature: false, pricingTemplateId })
        );
        if (mine === seq.current) {
          setDryRun(res.data);
          setDocSelection((prev) => reconcileSelection(res.data?.preview?.documents, prev));
        }
      } catch (err) {
        if (mine === seq.current) {
          const status = err.response?.status;
          const body = err.response?.data;
          // SOUND-784: a stale template pick (400 field:pricingTemplateId) on
          // the DRY RUN — clear it, say why, and re-check without one so the
          // preflight reflects the processor as it is now.
          if (pricingTemplateId !== null && isStaleTemplateError(status, body)) {
            setPickedTemplate(null);
            setTemplateNotice(body?.error || "That pricing template is no longer available. Choose another.");
            runDryRunRef.current?.({ mcc: selectedMcc, pricingTemplateId: null });
            return;
          }
          const e = describeTransmitError(status, body);
          if (e.kind === "already-transmitted") {
            setCheckError(e);
            onTransmittedRef.current?.();
          } else {
            setCheckError({ ...e, title: "Could not run the preflight check" });
          }
        }
      } finally {
        if (mine === seq.current) setChecking(false);
      }
    },
    [applicationId, effectiveCompanyId]
  );
  // Lets the stale-template path re-run itself without a self-referencing
  // useCallback dependency.
  const runDryRunRef = useRef(runDryRun);
  runDryRunRef.current = runDryRun;

  // Fresh state on every open: a dialog that remembers the last MCC or a stale
  // result is how the wrong thing gets sent.
  useEffect(() => {
    if (!open) return;
    setDryRun(null);
    setMcc(null);
    setSendForSignature(false);
    setConfirmOpen(false);
    setSendError(null);
    setSuccess(null);
    setPickedTemplate(null);
    setTemplateNotice(null);
    setDocSelection({});
    runDryRun();
  }, [open, runDryRun]);

  const close = useCallback(() => {
    if (sending) return;
    onClose?.();
  }, [sending, onClose]);

  // Escape closes — but not while the confirmation is up (it handles its own
  // Escape) and never mid-send.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !confirmOpen) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, confirmOpen, close]);

  if (!open || !application) return null;

  const processorHref = application.processorId
    ? `/settings/processors/${application.processorId}`
    : null;
  const view = buildPreflightView(dryRun, { processorHref });
  const templateOptions = transmitTemplateOptions(dryRun);
  const templateId = resolveTemplateSelection(
    templateOptions,
    pickedTemplate,
    dryRun?.selectedPricingTemplateId
  );
  const selectedTemplate = templateOptions.find((t) => t.templateId === templateId) || null;
  // Both required selections made, nothing else blocking, server agrees.
  const submitReady = canTransmit(view, { mccSelected: Boolean(mcc), templateSelected: Boolean(templateId) });
  // Requests carry the id exactly as the API sent it (a Number for 239).
  const requestTemplateId = (id) => templateIdForRequest(templateOptions, id);
  const preview = dryRun?.preview || null;
  const merchant = preview?.legalName || application.clientName || "this merchant";
  const dba = preview?.dbaName || application.dbaName;
  const busy = checking || sending;
  const previewDocs = preview?.documents || [];
  const docsSelectable = isSelectable(previewDocs);
  const docCount = selectionCount(previewDocs, docSelection);
  const toggleDoc = (fileId, checked) => setDocSelection((prev) => toggleSelection(prev, fileId, checked));

  const selectMcc = (record) => {
    setMcc(record);
    setSendError(null);
    runDryRun({ mcc: record, pricingTemplateId: requestTemplateId(pickedTemplate) });
  };

  const selectTemplate = (id) => {
    setPickedTemplate(id || null);
    setTemplateNotice(null);
    setSendError(null);
    runDryRun({ mcc, pricingTemplateId: requestTemplateId(id) });
  };

  const recheck = () => runDryRun({ mcc, pricingTemplateId: requestTemplateId(pickedTemplate) });

  const transmit = async () => {
    setConfirmOpen(false);
    setSending(true);
    setSendError(null);
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.stratusTransmit(applicationId, { live: true }),
        auth.user,
        effectiveCompanyId,
        transmitBody({
          mcc,
          sendForSignature,
          pricingTemplateId: requestTemplateId(templateId),
          fileIds: selectedFileIds(previewDocs, docSelection),
        })
      );
      setSuccess(describeTransmitSuccess(res.data));
      onTransmitted?.();
    } catch (err) {
      const status = err.response?.status;
      const e = describeTransmitError(status, err.response?.data, preview?.ownerNames || []);
      setSendError(e);
      if (e.refusal) setDryRun((prev) => mergeRefusal(prev, e.refusal));
      // Someone else got there first — the page should show the transmitted state.
      if (e.kind === "already-transmitted") onTransmitted?.();
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div
        className="surface flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl shadow-lg ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="stratus-transmit-title"
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border p-5">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
              <Send className="h-5 w-5" />
            </div>
            <div>
              <h3 id="stratus-transmit-title" className="text-lg font-semibold">
                Transmit to Kurv
              </h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {merchant}
                {dba && dba !== merchant ? ` · DBA ${dba}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="btn btn-ghost h-8 w-8 shrink-0 p-0"
            aria-label="Close"
            disabled={sending}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {success ? (
            <SuccessView success={success} />
          ) : (
            <>
              <ErrorPanel error={sendError} />

              {checkError && (
                <div className="space-y-2">
                  <ErrorPanel error={checkError} />
                  <button type="button" className="btn btn-outline" onClick={recheck} disabled={busy}>
                    Try again
                  </button>
                </div>
              )}

              {!dryRun && checking && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking the application against Kurv's requirements…
                </div>
              )}

              {dryRun && (
                <>
                  {/* Required selections (CPO): always-on form fields, not
                      errors. The dry run still reports them in `missing`;
                      buildPreflightView partitions them out of the red list. */}
                  <MccPicker
                    selected={mcc}
                    onSelect={selectMcc}
                    disabled={busy}
                    hint={view.selectionNeeds.mcc}
                  />

                  <section>
                    <Heading required>Pricing template</Heading>
                    {templateOptions.length > 0 && (
                      <select
                        className="select w-full py-1.5 text-sm"
                        value={templateId || ""}
                        onChange={(e) => selectTemplate(e.target.value)}
                        disabled={busy}
                      >
                        {!templateId && <option value="">Select a template…</option>}
                        {templateOptions.map((t) => (
                          <option key={t.templateId} value={t.templateId}>
                            {templateLabel(t)}
                            {t.isDefault ? " — default" : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    {templateNotice && (
                      <p className="mt-1 text-xs text-destructive" role="alert">
                        {templateNotice}
                      </p>
                    )}
                    {!templateId && !templateNotice && (
                      <RequiredHint href={view.selectionNeeds.pricingTemplate?.href || processorHref}>
                        {view.selectionNeeds.pricingTemplate?.reason ||
                          (templateOptions.length
                            ? "Required — choose a pricing template."
                            : "Required — no active pricing template is configured for this processor.")}
                      </RequiredHint>
                    )}
                  </section>

                  <section>
                    <div className="mb-2 flex items-center justify-between">
                      <Heading>Preflight</Heading>
                      {checking ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Re-checking…
                        </span>
                      ) : (
                        <button type="button" className="btn btn-ghost h-7 px-2 text-xs" onClick={recheck} disabled={busy}>
                          Re-check
                        </button>
                      )}
                    </div>
                    {view.blocking.length === 0 ? (
                      <div className="flex items-center gap-2 rounded-md bg-success/10 px-3 py-2 text-sm text-foreground ring-1 ring-success/30">
                        <CheckCircle2 className="h-4 w-4 text-success" />
                        No data problems found{!mcc || !templateId ? " — choose the MCC and pricing template above." : ". Kurv should accept this application."}
                      </div>
                    ) : (
                      <div className="rounded-lg bg-destructive/5 p-3 ring-1 ring-destructive/30">
                        <div className="mb-2 text-sm font-semibold text-foreground">
                          {view.blocking.length} item{view.blocking.length === 1 ? "" : "s"} must be fixed before sending
                        </div>
                        <IssueList items={view.blocking} tone="destructive" />
                      </div>
                    )}
                    {view.warnings.length > 0 && (
                      <div className="mt-3 rounded-lg bg-warning/5 p-3 ring-1 ring-warning/30">
                        <div className="mb-2 text-sm font-semibold text-foreground">
                          {view.warnings.length} warning{view.warnings.length === 1 ? "" : "s"} — will send, but check
                          {view.nameSplitCount > 0 &&
                            ` (${view.nameSplitCount} owner name${view.nameSplitCount === 1 ? "" : "s"} split automatically)`}
                        </div>
                        <IssueList items={view.warnings} tone="warning" />
                      </div>
                    )}
                  </section>

                  <section>
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={sendForSignature}
                        onChange={(e) => setSendForSignature(e.target.checked)}
                        disabled={busy}
                        className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
                      />
                      <span>
                        <span className="block text-sm font-medium text-foreground">Send for signature</span>
                        <span className="block text-xs text-muted-foreground">
                          Kurv emails the merchant the application to sign as soon as it is created.
                        </span>
                      </span>
                    </label>
                  </section>

                  <Preview
                    preview={preview}
                    skipped={normalizeSkipped(dryRun.documentsSkipped)}
                    selection={docSelection}
                    onToggle={toggleDoc}
                    disabled={busy}
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border p-4">
          {success ? (
            <button type="button" className="btn btn-primary" onClick={close}>
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-ghost" onClick={close} disabled={sending}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setConfirmOpen(true)}
                // After an upstream failure we cannot prove whether Kurv created
                // the deal, so no blind retry from here: close and reopen, which
                // re-runs the preflight (and gets a 409 if it did land).
                disabled={
                  busy ||
                  !submitReady ||
                  sendError?.kind === "already-transmitted" ||
                  sendError?.kind === "upstream"
                }
                title={
                  submitReady
                    ? "Review and send to Kurv"
                    : !mcc || !templateId
                    ? "Choose an MCC and a pricing template first"
                    : "Resolve every blocking item first"
                }
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Review & transmit"}
              </button>
            </>
          )}
        </div>

        <ConfirmDialog
          open={confirmOpen}
          size="md"
          title="Send this application to Kurv?"
          description={`This submits ${merchant}'s application to Kurv. It cannot be undone — once sent, the only correction is to withdraw it.`}
          confirmLabel="Transmit to Kurv"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={transmit}
        >
          <ul className="space-y-1 text-sm">
            <li>
              <span className="text-muted-foreground">MCC: </span>
              <span className="text-foreground">{mcc ? mccOptionLabel(mcc) : "—"}</span>
            </li>
            <li>
              <span className="text-muted-foreground">Pricing template: </span>
              <span className="text-foreground">{selectedTemplate ? templateLabel(selectedTemplate) : "—"}</span>
            </li>
            <li>
              <span className="text-muted-foreground">Documents: </span>
              <span className="text-foreground">
                {docsSelectable
                  ? `${docCount.selected} of ${docCount.total} selected`
                  : `${docCount.total} (all eligible)`}
              </span>
            </li>
            <li>
              <span className="text-muted-foreground">Signature request: </span>
              <span className="text-foreground">{sendForSignature ? "sent to the merchant" : "not sent"}</span>
            </li>
            {view.warnings.length > 0 && (
              <li className="text-warning">
                {view.warnings.length} warning{view.warnings.length === 1 ? "" : "s"} will travel with it.
              </li>
            )}
          </ul>
        </ConfirmDialog>
      </div>
    </div>,
    document.body
  );
}
